import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CardContent,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { MapPin, Phone, Clock3 } from 'lucide-react';
import { fetchSingleSpa, updateSpa, createSpa } from '../../api/spaApi';
import SpaFormModal from '../HotelInfo/SpaFormModal';
import OpeningHoursDisplay from '../ui/Calendar/OpeningHoursDisplay';
import LoadingPlaceholder from '../ui/Loading/LoadingPlaceholder';

export default function SpaProfile({ spaId, propertyId }) {
  const [spaData, setSpaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!spaId || !propertyId) return;
    setLoading(true);
    fetchSingleSpa(spaId, propertyId)
      .then(setSpaData)
      .catch(err => console.error('Failed to load spa:', err))
      .finally(() => setLoading(false));
  }, [spaId, propertyId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev =>
        spaData?.photos?.length ? (prev + 1) % spaData.photos.length : 0
      );
    }, 4000);
    return () => clearInterval(interval);
  }, [spaData?.photos]);

  if (loading) return <LoadingPlaceholder />;
  if (!spaData) return null;

  return (
    <Box sx={{ px: 0 }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 400, md: 480 },
          borderRadius: 3,
          overflow: 'hidden',
          mb: 6,
          pt: 0
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
          {spaData?.photos?.map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`Slide ${index + 1}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(0.7)',
                opacity: index === currentSlide ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
              }}
            />
          ))}
        </Box>

        <IconButton
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)'
            }
          }}
          onClick={() => {
            setForm(spaData);
            setOpen(true);
          }}
        >
          <Edit />
        </IconButton>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            color: '#fff',
            px: { xs: 3, md: 8 },
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {spaData?.name && (
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Welcome to <span style={{ color: '#deaf59ff' }}>{spaData.name}</span>
            </Typography>
          )}
          {spaData?.subtitle && (
            <Typography variant="body1" sx={{ mb: 3 }}>
              {spaData.subtitle}
            </Typography>
          )}
          <OpeningHoursDisplay openingHours={spaData.openingHours} />
        </Box>
      </Box>

      {/* About Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
          {spaData?.about && (
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
              {spaData.about}
            </Typography>
          )}
          {spaData?.address && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Address: {spaData.address}
            </Typography>
          )}
          {spaData?.phone && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Phone size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Phone: {spaData.phone}
            </Typography>
          )}
          {spaData?.email && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              📧 Email: {spaData.email}
            </Typography>
          )}
        </Box>
      </Box>

      {spaData?.updatedAt && (
        <Typography variant="caption" align="center" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
          Last updated: {new Date(spaData.updatedAt).toLocaleString()}
        </Typography>
      )}
      <SpaFormModal
        open={open}
        onClose={() => setOpen(false)}
        initialData={form}
        onSubmit={async (data) => {
          try {
            const saved = data._id
              ? await updateSpa(data._id, data)
              : await createSpa(data);

            setSpaData(saved);
            setOpen(false);
            setForm({});
          } catch (err) {
            console.error('Failed to save spa', err);
          }
        }}
      />
    </Box>
  );
}